<template>
  <div id="viva-app-root" class="flex flex-col items-center min-h-screen max-w-full mx-auto bg-black">
    <!-- Splash screen overlay -->
    <Transition name="viva-splash-fade">
      <div v-if="splashVisible" id="viva-splash">
        <img id="viva-splash-icon" src="/icons/vapp-192.png" alt="Viva"/>
        <div id="viva-splash-track">
          <div id="viva-splash-fill"></div>
        </div>
      </div>
    </Transition>

    <TheLoadingOverlay :is-navigating="isNavigating"/>
    <PwaInstallPrompt/>
    <div dir="rtl" class="flex flex-col min-h-screen w-full md:max-w-md mx-auto bg-black shadow-lg">
      <Html dir="rtl"/>
      <NuxtLayout>
        <NuxtPage/>
      </NuxtLayout>
    </div>
  </div>
</template>
<script lang="ts" setup>
import {safeInternalPath} from '~/utils/safeInternalPath'

const SPLASH_MIN_MS = 2_000

const splashVisible = ref(true)
const splashStart = Date.now()

// Android WebView keeps stale raster tiles from a removed full-screen layer, which
// leaves grey (#1e1e1e) rectangles over the black page. Nudging the background of
// the full-height root forces the whole viewport to be re-painted once.
const forceRepaint = () => {
  const root = document.getElementById('viva-app-root')
  if (!root) return
  root.style.backgroundColor = '#000001'
  requestAnimationFrame(() => {
    root.style.backgroundColor = ''
  })
}

onMounted(async () => {
  const remaining = SPLASH_MIN_MS - (Date.now() - splashStart)
  if (remaining > 0) await new Promise(r => setTimeout(r, remaining))
  splashVisible.value = false
  // splash leave transition is 0.4s; repaint once it is actually gone
  setTimeout(forceRepaint, 500)
})
import type {Wallet} from "~/types/models";

const userId = useAppCookie('userId')
const token = useAppCookie('token')
const route = useRoute()
const {$toast} = useNuxtApp()
const user = useLocalStorage('user', {})
const {isNavigating} = useNavigationLoading()
provide('navigationLoading', {isNavigating})

usePwaAutoUpdate()

useHead({
  htmlAttrs: {
    dir: 'rtl',
    lang: 'fa',
  },
  link: [
    {rel: 'manifest', href: '/manifest.json'},
  ],
  meta: [
    {name: 'mobile-web-app-capable', content: 'yes'},
    {name: 'apple-mobile-web-app-capable', content: 'yes'},
    {name: 'theme-color', content: '#1e1e1e'},
    {name: 'apple-mobile-web-app-status-bar-style', content: 'black'},
  ]
})

// Check userId only on client side, once
onMounted(() => {
  const publicRoutes = ['login', 'login-otp', 'register', 'register-otp']
  if ((!userId.value || !token.value) && !publicRoutes.includes(String(route.name))) {
    navigateTo({
      path: '/login',
      query: {next: safeInternalPath(route.fullPath, '/')},
    }, {replace: true})
  }
})

// Watch route changes for auth check
// watch(() => route.name, () => {
//   if (!userId.value && route.name !== 'login') {
//     navigateTo('/login')
//   }
// })

// Load wallets only once on mount, not reactively
const wallets = ref<Wallet[]>()
if (userId.value && !wallets.value) {
  wallets.value = await useWallets()
}

const setting = useLocalStorage('setting', {})
await useAsyncData('get-setting', getSetting, {
  transform: (data) => {
    const safeSettings = data && !Array.isArray(data) ? data : {}
    setting.value = safeSettings
    return safeSettings
  },
  lazy: true,
  server: false,
  dedupe: 'defer'
})

</script>

<style>
#viva-splash {
  position: fixed;
  inset: 0;
  /* must match the app background so any leftover paint is invisible */
  background: #000000;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

#viva-splash-icon {
  width: 88px;
  height: 88px;
  border-radius: 22px;
  margin-bottom: 36px;
  animation: vs-breathe 2.8s ease-in-out infinite;
}

#viva-splash-track {
  width: 150px;
  height: 3px;
  background: #2a2a2a;
  border-radius: 3px;
  overflow: hidden;
}

#viva-splash-fill {
  height: 100%;
  width: 5%;
  border-radius: 3px;
  background: linear-gradient(90deg, #fa7f60, #5c5c5c, #c8572f);
  background-size: 200% 100%;
  animation: vs-progress 8s ease-out forwards, vs-shimmer 1.6s linear infinite;
}

@keyframes vs-breathe {
  0%, 100% {
    transform: scale(1);
    filter: drop-shadow(0 0 0px rgba(255, 255, 255, 0));
  }
  50% {
    transform: scale(1.06);
    filter: drop-shadow(0 0 18px rgba(255, 255, 255, .12));
  }
}

@keyframes vs-progress {
  0% {
    width: 5%;
  }
  20% {
    width: 35%;
  }
  55% {
    width: 65%;
  }
  85% {
    width: 82%;
  }
  100% {
    width: 92%;
  }
}

@keyframes vs-shimmer {
  0% {
    background-position: -200% center;
  }
  100% {
    background-position: 200% center;
  }
}

.viva-splash-fade-leave-active {
  transition: opacity 0.4s ease;
}

.viva-splash-fade-leave-to {
  opacity: 0;
}
</style>
